import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  FormHelperText,
  InputAdornment,
  Autocomplete,
  Alert,
  Snackbar
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Close as CloseIcon } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { APP_SNACKBAR_CONTENT_SX, useAppSnackbarAnchorSx } from '@components/layout/appSnackbar'
import { useGetCigarLinesQuery, useGetBrandsQuery } from '@store/api/cigarApi'
import { useGetHumidorsQuery } from '@store/api/humidorApi'
import { useCreateBoxMutation, CigarBox } from '@store/api/boxApi'

interface BoxFormProps {
  open: boolean
  onClose: () => void
  box?: CigarBox | null
}

const schema = yup.object({
  brandId: yup.string().required('Brand is required'),
  cigarLineId: yup.string().required('Cigar line is required'),
  humidorId: yup.string().required('Humidor is required'),
  boxSize: yup.number().min(1, 'Box size must be at least 1').max(100, 'Box size cannot exceed 100').required('Box size is required'),
  cigarsPerBox: yup.number().min(1, 'Cigars per box must be at least 1').max(100, 'Cannot exceed 100').required('Cigars per box is required'),
  purchasePrice: yup.number().min(0, 'Price must be non-negative').max(50000, 'Price cannot exceed $50,000').nullable().transform((value, original) => original === '' ? null : value),
  purchaseDate: yup.date().nullable().transform((value, original) => original === '' ? null : value),
  purchaseLocation: yup.string().max(255, 'Location cannot exceed 255 characters').nullable(),
  notes: yup.string().max(500, 'Notes cannot exceed 500 characters').nullable(),
})

type BoxFormData = yup.InferType<typeof schema>

const BoxForm: React.FC<BoxFormProps> = ({ open, onClose, box }) => {
  const [selectedBrandId, setSelectedBrandId] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState('')

  const { data: brands = [], isLoading: brandsLoading } = useGetBrandsQuery({})
  const { data: cigarLines = [], isLoading: linesLoading } = useGetCigarLinesQuery(
    { brandId: selectedBrandId },
    { skip: !selectedBrandId }
  )
  const { data: humidorsData } = useGetHumidorsQuery()
  const humidors = humidorsData || []

  const [createBox, { isLoading: isCreating }] = useCreateBoxMutation()

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<BoxFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      brandId: '',
      cigarLineId: '',
      humidorId: '',
      boxSize: 25,
      cigarsPerBox: 25,
      purchasePrice: null,
      purchaseDate: null,
      purchaseLocation: '',
      notes: '',
    }
  })

  const watchBrandId = watch('brandId')
  const watchPurchasePrice = watch('purchasePrice')
  const watchCigarsPerBox = watch('cigarsPerBox')

  useEffect(() => {
    if (watchBrandId) {
      setSelectedBrandId(watchBrandId)
      setValue('cigarLineId', '')
    }
  }, [watchBrandId, setValue])

  const costPerCigar = watchPurchasePrice && watchCigarsPerBox
    ? (Number(watchPurchasePrice) / Number(watchCigarsPerBox)).toFixed(2)
    : null

  const snackbarAnchorSx = useAppSnackbarAnchorSx()

  const handleClose = () => {
    reset()
    setSelectedBrandId('')
    onClose()
  }

  const onSubmit = async (data: BoxFormData) => {
    try {
      await createBox({
        cigarLineId: data.cigarLineId,
        humidorId: data.humidorId,
        boxSize: data.boxSize,
        cigarsPerBox: data.cigarsPerBox,
        purchasePrice: data.purchasePrice ?? undefined,
        purchaseDate: data.purchaseDate ? data.purchaseDate.toISOString() : undefined,
        purchaseLocation: data.purchaseLocation ?? undefined,
        notes: data.notes ?? undefined,
      }).unwrap()
      setSuccessMessage('Box added successfully!')
      handleClose()
    } catch (error: any) {
      console.error('Failed to create box:', error)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {box ? 'Edit Box' : 'Add Box to Collection'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {/* Brand Selection */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="brandId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={brands}
                      getOptionLabel={(option) => option.name}
                      loading={brandsLoading}
                      value={brands.find(b => b.id === field.value) || null}
                      onChange={(_, newValue) => {
                        field.onChange(newValue?.id || '')
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Brand"
                          error={!!errors.brandId}
                          helperText={errors.brandId?.message}
                          required
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              {/* Cigar Line Selection */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="cigarLineId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={cigarLines}
                      getOptionLabel={(option) => option.name}
                      loading={linesLoading}
                      disabled={!selectedBrandId}
                      value={cigarLines.find(l => l.id === field.value) || null}
                      onChange={(_, newValue) => {
                        field.onChange(newValue?.id || '')
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cigar Line"
                          error={!!errors.cigarLineId}
                          helperText={errors.cigarLineId?.message || (!selectedBrandId ? 'Select a brand first' : '')}
                          required
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              {/* Humidor Selection */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="humidorId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.humidorId} required>
                      <InputLabel>Humidor</InputLabel>
                      <Select {...field} label="Humidor">
                        {(Array.isArray(humidors) ? humidors : []).map((h: any) => (
                          <MenuItem key={h.id} value={h.id}>
                            {h.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.humidorId && (
                        <FormHelperText>{errors.humidorId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Box Size */}
              <Grid item xs={12} sm={3}>
                <Controller
                  name="boxSize"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Box Size"
                      type="number"
                      fullWidth
                      required
                      error={!!errors.boxSize}
                      helperText={errors.boxSize?.message}
                      inputProps={{ min: 1, max: 100 }}
                    />
                  )}
                />
              </Grid>

              {/* Cigars Per Box */}
              <Grid item xs={12} sm={3}>
                <Controller
                  name="cigarsPerBox"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cigars Per Box"
                      type="number"
                      fullWidth
                      required
                      error={!!errors.cigarsPerBox}
                      helperText={errors.cigarsPerBox?.message}
                      inputProps={{ min: 1, max: 100 }}
                    />
                  )}
                />
              </Grid>

              {/* Purchase Price */}
              <Grid item xs={12} sm={4}>
                <Controller
                  name="purchasePrice"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Purchase Price"
                      type="number"
                      fullWidth
                      error={!!errors.purchasePrice}
                      helperText={errors.purchasePrice?.message}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  )}
                />
              </Grid>

              {/* Cost Per Cigar (calculated, read-only) */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Cost Per Cigar"
                  value={costPerCigar ? `$${costPerCigar}` : '—'}
                  fullWidth
                  disabled
                  helperText="Auto-calculated"
                />
              </Grid>

              {/* Purchase Date */}
              <Grid item xs={12} sm={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    name="purchaseDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Purchase Date"
                        value={field.value || null}
                        onChange={(date) => field.onChange(date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.purchaseDate,
                            helperText: errors.purchaseDate?.message as string,
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Purchase Location */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="purchaseLocation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Purchase Location"
                      fullWidth
                      error={!!errors.purchaseLocation}
                      helperText={errors.purchaseLocation?.message}
                    />
                  )}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Notes"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.notes}
                      helperText={errors.notes?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCreating}
            >
              {isCreating ? 'Adding...' : 'Add Box'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={snackbarAnchorSx}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" variant="filled" sx={APP_SNACKBAR_CONTENT_SX}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default BoxForm
